import React, { useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import authorizationAgreementApi, {
  Agreement,
  AgreementSignability,
} from '../../../utils/api/authorizationAgreement';
import AgreementActionConfirmationModal, {
  AgreementAction,
} from './AgreementActionConfirmationModal';

interface Props {
  projectId: number;
  agreement: Agreement | null;
  signability: AgreementSignability;
  onSignNow: () => void;
  onDownload: () => void;
  onAgreementUpdated: () => void;
}

const AuthorizationAgreementBanner: React.FC<Props> = ({
  projectId,
  agreement,
  signability,
  onSignNow,
  onDownload,
  onAgreementUpdated,
}) => {
  const [busy, setBusy] = useState(false);
  const [pendingAction, setPendingAction] = useState<AgreementAction | null>(
    null,
  );

  // SW2-2336 — a part-signed agreement is also a dead end today: signBlock refuses to
  // re-sign a block that already carries a timestamp, so clearing has to be offered
  // whenever there is any signature at all, not only once executed.
  const hasAnySignature = !!(
    agreement?.client_signed_at || agreement?.inspector_signed_at
  );
  const isExecuted = agreement?.status === 'executed';
  const canClear = signability.canWaive && hasAnySignature;

  // All three actions reverse a compliance decision and record an audit event, so they
  // share one confirm-with-reason dialog and one busy/error path.
  const ACTIONS: Record<
    AgreementAction,
    { run: (reason: string) => Promise<unknown>; success: string; failure: string }
  > = {
    clear: {
      run: (reason) =>
        authorizationAgreementApi.clearAgreement(projectId, reason),
      success:
        'Signatures cleared — the agreement can now be edited and signed again',
      failure: 'Error clearing signatures',
    },
    waive: {
      run: (reason) =>
        authorizationAgreementApi.waiveRequirement(projectId, reason),
      success:
        'Authorization & Agreement marked as not required for this project',
      failure: 'Error updating requirement',
    },
    unwaive: {
      run: (reason) =>
        authorizationAgreementApi.unwaiveRequirement(projectId, reason),
      success: 'Authorization & Agreement requirement restored',
      failure: 'Error updating requirement',
    },
  };

  const runAction = async (action: AgreementAction, reason: string) => {
    setBusy(true);
    try {
      await ACTIONS[action].run(reason);
      setPendingAction(null);
      onAgreementUpdated();
      toast.success(ACTIONS[action].success);
    } catch (err) {
      const error = err as any;

      toast.error(
        error?.response?.data?.error || ACTIONS[action].failure,
      );
    } finally {
      setBusy(false);
    }
  };

  const actionButton = (
    action: AgreementAction,
    label: string,
    color: string,
  ) => (
    <button
      type='button'
      onClick={() => setPendingAction(action)}
      disabled={busy}
      style={{
        background: 'none',
        border: `1px solid ${color}`,
        color,
        padding: '5px 12px',
        borderRadius: '4px',
        fontSize: '12px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );

  const confirmModal = pendingAction ? (
    <AgreementActionConfirmationModal
      action={pendingAction}
      isExecuted={isExecuted}
      submitting={busy}
      onClose={() => setPendingAction(null)}
      onConfirm={(reason) => runAction(pendingAction, reason)}
    />
  ) : null;

  // Waived state
  if (agreement?.waived_at) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f5f5f5',
          border: '1px solid #ddd',
          borderLeft: '3px solid #999',
          borderRadius: '4px',
          padding: '9px 14px',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#555', fontWeight: 500 }}>
          Authorization &amp; Agreement
          <span style={{ background: '#888', color: '#fff', fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' }}>
            Not Required
          </span>
          <span style={{ fontSize: '11px', color: '#777', fontWeight: 400 }}>
            · Marked not required {moment(agreement.waived_at).format('MMM D, YYYY')}
          </span>
        </div>
        {signability.canWaive &&
          actionButton('unwaive', 'Require again', '#555')}
        {confirmModal}
      </div>
    );
  }

  // Executed state
  if (agreement?.status === 'executed') {
    const clientAt = agreement.client_signed_at ? new Date(agreement.client_signed_at) : null;
    const inspectorAt = agreement.inspector_signed_at ? new Date(agreement.inspector_signed_at) : null;
    const signedAt =
      clientAt && inspectorAt
        ? clientAt > inspectorAt ? clientAt : inspectorAt
        : clientAt ?? inspectorAt;
    const signedLabel = signedAt ? moment(signedAt).format('MMM D, YYYY') : null;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f0faf6',
          borderLeft: '3px solid #1D9E75',
          border: '1px solid #b8e8d4',
          borderRadius: '4px',
          padding: '9px 14px',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#085041', fontWeight: 500 }}>
          Authorization &amp; Agreement
          <span style={{ background: '#1D9E75', color: '#fff', fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' }}>
            Executed
          </span>
          {signedLabel && (
            <span style={{ fontSize: '11px', color: '#0F6E56', fontWeight: 400 }}>
              · Signed {signedLabel}
            </span>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          {/* A real button, not a click-handled span: this needs to be reachable by
              keyboard and announced as a control. Styled to read as a link. */}
          <button
            type='button'
            onClick={onDownload}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              fontSize: '12px',
              color: '#1D9E75',
              textDecoration: 'underline',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Download PDF
          </button>
          {canClear && actionButton('clear', 'Clear signatures', '#0F6E56')}
        </div>
        {confirmModal}
      </div>
    );
  }

  // Pending / unsigned state
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: '#fffbf2',
        borderLeft: '3px solid #BA7517',
        border: '1px solid #fad68a',
        borderRadius: '4px',
        padding: '9px 14px',
        marginBottom: '16px',
      }}
    >
      <div>
        <div style={{ fontSize: '13px', color: '#633806', fontWeight: 500 }}>
          Authorization &amp; Agreement · Signature required
        </div>
        <div style={{ fontSize: '11px', color: '#854F0B', marginTop: '2px' }}>
          Inspections are locked until both parties have signed
        </div>
      </div>
      <div
        style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}
      >
        {canClear && actionButton('clear', 'Clear signatures', '#BA7517')}
        {signability.canWaive &&
          actionButton('waive', 'Not required for this project', '#633806')}
        <button
          type='button'
          onClick={onSignNow}
          style={{ background: '#1D9E75', color: '#fff', border: 'none', padding: '7px 16px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}
        >
          Sign now
        </button>
      </div>
      {confirmModal}
    </div>
  );
};

export default AuthorizationAgreementBanner;
